/**
 * §5 Permission Matrix — Nymbl Staff System Design v3.0
 *
 * This module is the single source of truth for role × capability access.
 * Every route guard must consult this matrix rather than hand-coding role checks.
 *
 * Encoding rules (from §5 spec table):
 *   "✓"         → { kind: "any" }     — unrestricted access for the role
 *   "own"        → { kind: "ownSolution" } or { kind: "self" } per row semantics
 *   "own Accounts" → { kind: "ownAccount" }
 *   "self only"  → { kind: "self" }
 *   blank / "—"  → capability omitted from the role's record (denied)
 *   "suggest only" → omitted (SYSTEM cannot write, only suggest; enforced at route layer)
 *   "confirm"    → { kind: "any" } for RM (RM writes rm_confirmed_rating)
 *   "self submit" → { kind: "self" } for SA (SA writes self_rating on own resource)
 */

import type { NymblRole } from "@/lib/auth";

// ── Capability registry ──────────────────────────────────────────────────────

export const CAPABILITIES = [
  /**
   * Solutions Dashboard — view the triage hub.
   * CEO/RM: any. SA: own Solutions. ACCT: own Accounts. FIN: any.
   */
  "viewSolutionsDashboard",

  /**
   * Solution Detail — view a single solution's full operational profile.
   * CEO/RM: any. SA: own Solutions. ACCT: own Accounts. FIN: any.
   */
  "viewSolutionDetail",

  /**
   * Configure Pod Size on Solution — set composition (Solo / Pair / Trio).
   * RM only. SYSTEM: suggest only (omitted; not a write capability for SYSTEM).
   */
  "configurePodSize",

  /**
   * Override Pod Size from tier default — override with an explicit reason.
   * RM only.
   */
  "overridePodSizeFromTierDefault",

  /**
   * Assign / change R1, R2, R3 — set or swap Primary assignments.
   * RM only. SYSTEM: suggest only (omitted).
   */
  "assignPrimary",

  /**
   * Assign / change Supporting — add or remove Supporting assignments.
   * RM only. SYSTEM: suggest only (omitted).
   */
  "assignSupporting",

  /**
   * View Resource Directory — browse the bench.
   * CEO/RM: any. SA: self row only. FIN: any. ACCT: denied.
   */
  "viewResourceDirectory",

  /**
   * View Resource Detail — full profile of one Resource.
   * CEO/RM: any. SA: self only. FIN: any. ACCT: denied.
   */
  "viewResourceDetail",

  /**
   * Edit Resource constraint ratings.
   * RM: confirm path (any scope on rm_confirmed_rating).
   * SA: self-submit path (self scope on self_rating).
   * Route layer enforces which column each role writes.
   */
  "editResourceConstraintRating",

  /**
   * Edit Resource competency grades (proficiency 1–5).
   * RM only.
   */
  "editResourceCompetencyProficiency",

  /**
   * Assign / modify / confirm a Resource's Discipline Rating (1–5) — the
   * Expectation Match input (docs/specs/discipline-rating-fit-score.md).
   * RM only. Unlike editResourceConstraintRating, there is NO self-submit
   * path for SA — D-0715-E removed SA self-submission from this flow, so SA
   * is intentionally absent from this capability's scope entirely.
   */
  "editResourceDisciplineRating",

  /**
   * Edit Resource capacity (point budget).
   * RM only.
   */
  "editResourceCapacity",

  /**
   * View Assignments Ops page — cross-book assignment view.
   * CEO/RM: any. ACCT: own Accounts. FIN: any. SA: denied.
   */
  "viewAssignmentsOps",

  /**
   * View Schedule Heatmap.
   * CEO/RM: any. SA: self row only. ACCT: own Account rows. FIN: any.
   */
  "viewScheduleHeatmap",

  /**
   * View Fit Calculator — transparency breakdown per Solution.
   * CEO/RM: any. SA: own Solutions. ACCT: own Accounts. FIN: denied.
   */
  "viewFitCalculator",

  /**
   * Edit Fit Score weights — per-Solution weight override.
   * RM only.
   */
  "editFitScoreWeights",

  /**
   * Manage Accounts — CRUD on client accounts (profile: name, industry,
   * externalNymblAccountId). Also covers reading the Account list and detail.
   * RM: any. ACCT: own Accounts (direct edit — no gate, per §9.5 Account Mgmt resolution).
   */
  "manageAccounts",

  /**
   * Set Account Expectation Level.
   * RM-only per the Account Management spec (scoring input, not an ACCT concern).
   * ACCT does NOT hold this capability.
   */
  "setAccountExpectationLevel",

  /**
   * Set Account Engagement Standard Tier.
   * RM: any. ACCT: own Accounts (direct edit — no gate, per §9.5 Account Mgmt resolution).
   */
  "setAccountEngagementStandardTier",

  /**
   * Edit Account constraint profile — add / remove constraints on an Account.
   * RM: any. ACCT: own Accounts (direct edit — no gate, per Account Mgmt spec locked decision #1).
   * Editing constraints does NOT re-cascade to existing Solutions.
   */
  "editAccountConstraints",

  /**
   * Manage Configuration — constraints, intensity levels, tier defaults.
   * RM only.
   */
  "manageConfiguration",

  /**
   * Run Hours sync — trigger nightly Nymbl hours pull.
   * RM: any. FIN: any. SYSTEM: scheduled.
   */
  "runHoursSync",

  /**
   * Run import from Nymbl — persons / accounts / solutions.
   * RM: any. SYSTEM: scheduled.
   */
  "runImportFromNymbl",

  /**
   * View Reporting & Analytics.
   * CEO/RM: any. ACCT: own Accounts. FIN: any. SA: denied.
   */
  "viewReportingAnalytics",

  /**
   * View Audit Trail.
   * CEO/RM: any. FIN: any. SA/ACCT: denied.
   */
  "viewAuditTrail",

  /**
   * View Staffing Requests queue + detail (docs/specs/staffing-requests.md).
   * CEO/RM: any. ACCT: own Accounts. FIN: any. SA: denied.
   */
  "viewStaffingRequests",

  /**
   * Submit a Staffing Request (file upload / manual lines).
   * RM: any. ACCT: own Accounts (submit + track; cannot map or commit).
   */
  "submitStaffingRequest",

  /**
   * Map a Staffing Request — link to a Solution, confirm line→Discipline/position
   * mappings, resolve Resources. RM only.
   */
  "mapStaffingRequest",

  /**
   * Commit a Staffing Request to a Solution's Pod (writes assignments + schedules,
   * confirms Intensity, triggers Fit recalc). RM only.
   */
  "commitStaffingRequest",

  /**
   * Delete a Solution — hard, force-cascade removal for data cleanup.
   * RM only. SYSTEM excluded (manual action). Destructive + audited
   * (`solution_deleted`). See docs/specs/delete-solution.md.
   */
  "deleteSolution",

  /**
   * View Signals — access the signals triage hub (suggested Intensity, Pod changes).
   * CEO/RM: any. SA/ACCT/FIN: denied.
   */
  "viewSignals",

  /**
   * Process Solution On Demand — trigger on-demand Fit recalc for a Solution
   * (from pending Signals).
   * RM only.
   */
  "processSolutionOnDemand",

  /**
   * Confirm Suggested Intensity — mark a suggested Intensity signal as confirmed,
   * locking it to the Solution.
   * RM only.
   */
  "confirmSuggestedIntensity",

  /**
   * Apply Suggested Pod Change — accept a suggested Pod composition or assignment
   * change and commit it.
   * RM only.
   */
  "applySuggestedPodChange",

  /**
   * Apply Add Resource — apply a resource_off_pod Signal by adding the suggested
   * off-Pod Resource to the Pod (a Primary Position or Supporting) via the normal
   * audited staffing path.
   * RM only.
   */
  "applyAddResource",

  /**
   * Dismiss Signal — dismiss a Signal as non-actionable or false-positive.
   * RM only.
   */
  "dismissSignal",

  /**
   * View System Logs — read the technical observability log (errors + API
   * interactions) on the System → Logs page. Read-only surface.
   * CEO/RM: any. SA/ACCT/FIN: denied (logs expose paths, actor ids, and stack
   * traces across the whole book). Distinct from viewAuditTrail (business audit).
   * See docs/specs/system-logging.md.
   */
  "viewSystemLogs",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

// ── Scope types ──────────────────────────────────────────────────────────────

export type Scope =
  /** Unrestricted access — role can read/write any record of this type. */
  | { kind: "any" }
  /** SA accessing their own Resource row only. */
  | { kind: "self" }
  /** ACCT accessing Accounts/Solutions they own. */
  | { kind: "ownAccount" }
  /** SA accessing Solutions where their linked Resource is a Primary (R1/R2/R3). */
  | { kind: "ownSolution" }
  /** Explicitly denied (useful as a marker; absence from the record also means denied). */
  | { kind: "denied" };

// ── Permission Matrix ─────────────────────────────────────────────────────────
//
// Keyed by role. Capabilities absent from a role's record are denied.
// Mirrors §5 row-by-row. Column order in comments matches the spec table.

export const PERMISSION_MATRIX: Record<NymblRole | "SYSTEM", Partial<Record<Capability, Scope>>> = {
  //
  // ── CEO ────────────────────────────────────────────────────────────────────
  // Portfolio visibility. Read-only across the book. No write capabilities.
  //
  CEO: {
    viewSolutionsDashboard: { kind: "any" },
    viewSolutionDetail: { kind: "any" },
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    viewResourceDirectory: { kind: "any" },
    viewResourceDetail: { kind: "any" },
    // editResourceConstraintRating: denied
    // editResourceCompetencyProficiency: denied
    // editResourceDisciplineRating: denied
    // editResourceCapacity: denied
    viewAssignmentsOps: { kind: "any" },
    viewScheduleHeatmap: { kind: "any" },
    viewFitCalculator: { kind: "any" },
    // editFitScoreWeights: denied
    // manageAccounts: denied
    // setAccountExpectationLevel: denied
    // setAccountEngagementStandardTier: denied
    // CEO manages workspace configuration (Settings → config tabs + Integrations
    // list/refresh). Granted per product decision 2026-06-29 amending §5's
    // read-only-CEO rule; runImportFromNymbl deliberately stays RM-only.
    manageConfiguration: { kind: "any" },
    // runHoursSync: denied
    // runImportFromNymbl: denied
    viewReportingAnalytics: { kind: "any" },
    viewAuditTrail: { kind: "any" },
    // CEO reads the Staffing Requests queue + Decision Log; no write.
    viewStaffingRequests: { kind: "any" },
    // submitStaffingRequest: denied
    // mapStaffingRequest: denied
    // commitStaffingRequest: denied
    // CEO views the Signals triage hub.
    viewSignals: { kind: "any" },
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
    // CEO reads the System → Logs observability surface.
    viewSystemLogs: { kind: "any" },
  },

  //
  // ── RM ─────────────────────────────────────────────────────────────────────
  // Owns staffing decisions. Full write across the book.
  //
  RM: {
    viewSolutionsDashboard: { kind: "any" },
    viewSolutionDetail: { kind: "any" },
    configurePodSize: { kind: "any" },
    overridePodSizeFromTierDefault: { kind: "any" },
    assignPrimary: { kind: "any" },
    assignSupporting: { kind: "any" },
    viewResourceDirectory: { kind: "any" },
    viewResourceDetail: { kind: "any" },
    // RM confirms ratings (writes rm_confirmed_rating). Scope "any" — RM can
    // confirm any Resource's ratings, not just their own.
    editResourceConstraintRating: { kind: "any" },
    editResourceCompetencyProficiency: { kind: "any" },
    // RM assigns/modifies/confirms Discipline Ratings for any Resource. No SA
    // self-submit scope exists for this capability (D-0715-E).
    editResourceDisciplineRating: { kind: "any" },
    editResourceCapacity: { kind: "any" },
    viewAssignmentsOps: { kind: "any" },
    viewScheduleHeatmap: { kind: "any" },
    viewFitCalculator: { kind: "any" },
    editFitScoreWeights: { kind: "any" },
    manageAccounts: { kind: "any" },
    setAccountExpectationLevel: { kind: "any" },
    setAccountEngagementStandardTier: { kind: "any" },
    editAccountConstraints: { kind: "any" },
    manageConfiguration: { kind: "any" },
    runHoursSync: { kind: "any" },
    runImportFromNymbl: { kind: "any" },
    viewReportingAnalytics: { kind: "any" },
    viewAuditTrail: { kind: "any" },
    // RM owns the Staffing Requests workflow end-to-end.
    viewStaffingRequests: { kind: "any" },
    submitStaffingRequest: { kind: "any" },
    mapStaffingRequest: { kind: "any" },
    commitStaffingRequest: { kind: "any" },
    deleteSolution: { kind: "any" },
    // RM manages the Signals triage hub: view, process, confirm, apply, dismiss.
    viewSignals: { kind: "any" },
    processSolutionOnDemand: { kind: "any" },
    confirmSuggestedIntensity: { kind: "any" },
    applySuggestedPodChange: { kind: "any" },
    applyAddResource: { kind: "any" },
    dismissSignal: { kind: "any" },
    // RM reads the System → Logs observability surface.
    viewSystemLogs: { kind: "any" },
  },

  //
  // ── SA ─────────────────────────────────────────────────────────────────────
  // Own profile, own assignments, own ratings (self-submit only).
  //
  SA: {
    // SA sees own Solutions only (Solutions where their Resource is a Primary).
    viewSolutionsDashboard: { kind: "ownSolution" },
    viewSolutionDetail: { kind: "ownSolution" },
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    // SA sees only their own Resource row.
    viewResourceDirectory: { kind: "self" },
    viewResourceDetail: { kind: "self" },
    // SA self-submits constraint ratings (writes self_rating on own Resource).
    editResourceConstraintRating: { kind: "self" },
    // editResourceCompetencyProficiency: denied
    // editResourceDisciplineRating: denied
    // editResourceCapacity: denied
    // viewAssignmentsOps: denied
    // SA sees only their own row in the heatmap.
    viewScheduleHeatmap: { kind: "self" },
    // SA sees Fit Calculator for own Solutions only.
    viewFitCalculator: { kind: "ownSolution" },
    // viewStaffingRequests: denied (SA is not part of the staffing-request workflow)
    // submitStaffingRequest / mapStaffingRequest / commitStaffingRequest: denied
    // editFitScoreWeights: denied
    // manageAccounts: denied
    // setAccountExpectationLevel: denied
    // setAccountEngagementStandardTier: denied
    // manageConfiguration: denied
    // runHoursSync: denied
    // runImportFromNymbl: denied
    // viewReportingAnalytics: denied
    // viewAuditTrail: denied
    // viewSignals: denied
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
  },

  //
  // ── ACCT ───────────────────────────────────────────────────────────────────
  // Account Manager. Per §9.5 Account Management resolution (2026-06-29):
  // ACCT can directly edit profile + constraints on their own Accounts.
  // Expectation Level is RM-only (scoring input, not an ACCT concern).
  //
  ACCT: {
    // ACCT sees Solutions under their own Accounts.
    viewSolutionsDashboard: { kind: "ownAccount" },
    viewSolutionDetail: { kind: "ownAccount" },
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    // viewResourceDirectory: denied (spec: blank)
    // viewResourceDetail: denied (spec: blank)
    // editResourceConstraintRating: denied
    // editResourceCompetencyProficiency: denied
    // editResourceDisciplineRating: denied
    // editResourceCapacity: denied
    // ACCT sees Assignments for their Accounts.
    viewAssignmentsOps: { kind: "ownAccount" },
    // ACCT sees heatmap rows for their Account's Solutions.
    viewScheduleHeatmap: { kind: "ownAccount" },
    // ACCT sees Fit Calculator for own Accounts' Solutions.
    viewFitCalculator: { kind: "ownAccount" },
    // editFitScoreWeights: denied
    // Per §9.5 resolution: ACCT edits own Account profile directly — no gate.
    manageAccounts: { kind: "ownAccount" },
    // setAccountExpectationLevel: denied — RM-only (scoring input).
    // Per §9.5 resolution: ACCT sets tier on own Accounts directly — no gate.
    setAccountEngagementStandardTier: { kind: "ownAccount" },
    // Per Account Mgmt spec locked decision #1: ACCT edits own Account constraints directly.
    editAccountConstraints: { kind: "ownAccount" },
    // manageConfiguration: denied
    // runHoursSync: denied (spec: blank)
    // runImportFromNymbl: denied
    // ACCT sees Reporting for own Accounts.
    viewReportingAnalytics: { kind: "ownAccount" },
    // viewAuditTrail: denied
    // ACCT submits + tracks requests for own Accounts; cannot map or commit
    // (decision confirmed with the user: submit + read-only).
    viewStaffingRequests: { kind: "ownAccount" },
    submitStaffingRequest: { kind: "ownAccount" },
    // mapStaffingRequest: denied
    // commitStaffingRequest: denied
    // viewSignals: denied
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
  },

  //
  // ── FIN ────────────────────────────────────────────────────────────────────
  // Finance / Ops. Utilization, hours, reconciliation. Read-only on staffing.
  //
  FIN: {
    viewSolutionsDashboard: { kind: "any" },
    viewSolutionDetail: { kind: "any" },
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    viewResourceDirectory: { kind: "any" },
    viewResourceDetail: { kind: "any" },
    // editResourceConstraintRating: denied
    // editResourceCompetencyProficiency: denied
    // editResourceDisciplineRating: denied
    // editResourceCapacity: denied
    viewAssignmentsOps: { kind: "any" },
    viewScheduleHeatmap: { kind: "any" },
    // viewFitCalculator: denied (spec: blank for FIN)
    // editFitScoreWeights: denied
    // manageAccounts: denied
    // setAccountExpectationLevel: denied
    // setAccountEngagementStandardTier: denied
    // manageConfiguration: denied
    // Spec grants FIN runHoursSync.
    runHoursSync: { kind: "any" },
    // runImportFromNymbl: denied
    viewReportingAnalytics: { kind: "any" },
    viewAuditTrail: { kind: "any" },
    // FIN reads the Staffing Requests queue + Decision Log; no write.
    viewStaffingRequests: { kind: "any" },
    // submitStaffingRequest / mapStaffingRequest / commitStaffingRequest: denied
    // viewSignals: denied
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
  },

  //
  // ── SYSTEM ─────────────────────────────────────────────────────────────────
  // Cron jobs, schedulers, recalc triggers. Bypasses scope but only on
  // capabilities the spec matrix marks as "scheduled".
  // "suggest only" capabilities are intentionally omitted — suggestions are not
  // writes; they are handled at the application layer, not the permission layer.
  //
  SYSTEM: {
    runHoursSync: { kind: "any" },
    runImportFromNymbl: { kind: "any" },
  },
};
